import React, { useState, useEffect } from 'react';
import { Save, Eye, Megaphone, Edit, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const EMPTY_FORM = {
  title: '',
  body: '',
  start_date: '',
  end_date: '',
  color: 'green',
  link_url: '',
  status: 'active',
};

const COLOR_OPTIONS = [
  { value: 'green',  label: 'Green',  bg: '#10614E', text: '#FFFFFF' },
  { value: 'yellow', label: 'Yellow', bg: '#FCD34D', text: '#1A1A1A' },
  { value: 'red',    label: 'Red',    bg: '#EF4444', text: '#FFFFFF' },
  { value: 'blue',   label: 'Blue',   bg: '#3B82F6', text: '#FFFFFF' },
];

const COMMON_EMOJIS = [
  '🎓', '📚', '💰', '🎉', '⚡', '🔥', '✨',
  '🎯', '📢', '🏆', '💡', '🚀', '⏰', '📅', '🎊',
];

const toDatetimeLocal = (isoString) => {
  if (!isoString) return '';
  try {
    return new Date(isoString).toISOString().slice(0, 16);
  } catch {
    return '';
  }
};

const AnnouncementBarCMS = () => {
  const [loading, setLoading] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [charCount, setCharCount] = useState(0);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      const response = await api.get('/api/admin/announcements');
      setAnnouncements(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load announcements:', err);
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setCharCount(0);
    setErrors({});
    setShowEmojiPicker(false);
  };

  const handleInputChange = (field, value) => {
    if (field === 'body') {
      if (value.length > 200) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const insertEmoji = (emoji) => {
    if (formData.body.length + emoji.length <= 200) {
      handleInputChange('body', formData.body + emoji);
    }
    setShowEmojiPicker(false);
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.title.trim()) errs.title = 'Title is required';
    if (!formData.body.trim()) errs.body = 'Body is required';
    if (!formData.start_date) errs.start_date = 'Start date is required';
    if (!formData.end_date) errs.end_date = 'End date is required';
    if (formData.start_date && formData.end_date) {
      if (new Date(formData.end_date) < new Date(formData.start_date)) {
        errs.end_date = 'End date must be after start date';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleEdit = (announcement) => {
    setEditId(announcement.id);
    setFormData({
      title: announcement.title || '',
      body: announcement.body || '',
      start_date: toDatetimeLocal(announcement.start_date),
      end_date: toDatetimeLocal(announcement.end_date),
      color: announcement.color || 'green',
      link_url: announcement.link_url || '',
      status: announcement.status || 'active',
    });
    setCharCount((announcement.body || '').length);
    setErrors({});
  };

  const handleCancelEdit = () => {
    setEditId(null);
    resetForm();
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/announcement/archive/${id}`);
      toast.success('Announcement deleted');
      setDeleteConfirm(null);
      await loadAnnouncements();
    } catch (err) {
      console.error('Failed to delete announcement:', err);
      toast.error('Failed to delete announcement');
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
      };
      if (editId) {
        await api.patch(`/api/announcement/update/${editId}`, payload);
        toast.success('Announcement updated successfully');
        setEditId(null);
      } else {
        await api.post('/api/announcement/create', payload);
        toast.success('Announcement created successfully');
      }
      resetForm();
      await loadAnnouncements();
    } catch (err) {
      console.error('Failed to save announcement:', err);
      toast.error(editId ? 'Failed to update announcement' : 'Failed to create announcement');
    } finally {
      setLoading(false);
    }
  };

  const selectedColor = COLOR_OPTIONS.find((c) => c.value === formData.color);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#1A1A1A' }}>
            Announcement Bar Management
          </h1>
          <p style={{ color: '#6B7280' }}>Control the site-wide announcement banner</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor Panel */}
        <div
          className="bg-white p-6"
          style={{ borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #E2E5E7' }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Megaphone size={20} style={{ color: '#10614E' }} />
              <h2 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>
                {editId ? 'Edit Announcement' : 'Create Announcement'}
              </h2>
            </div>
            {editId && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="flex items-center gap-1 text-xs px-3 py-1 rounded"
                style={{ backgroundColor: '#F5F7F8', color: '#6B7280' }}
                data-testid="cancel-edit-btn"
              >
                <X size={14} />
                Cancel Edit
              </button>
            )}
          </div>

          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., Important Notice"
                className="w-full px-4 py-2 text-sm"
                style={{
                  border: errors.title ? '1px solid #EF4444' : '1px solid #E2E5E7',
                  borderRadius: '6px',
                  color: '#1A1A1A',
                }}
                data-testid="announcement-title-input"
              />
              {errors.title && (
                <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.title}</p>
              )}
            </div>

            {/* Body with Emoji */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium" style={{ color: '#1A1A1A' }}>
                  Message
                </label>
                <span className="text-xs" style={{ color: charCount > 180 ? '#EF4444' : '#6B7280' }}>
                  {charCount}/200
                </span>
              </div>
              <textarea
                value={formData.body}
                onChange={(e) => handleInputChange('body', e.target.value)}
                placeholder="Your announcement message..."
                rows={3}
                className="w-full px-4 py-2 text-sm"
                style={{
                  border: errors.body ? '1px solid #EF4444' : '1px solid #E2E5E7',
                  borderRadius: '6px',
                  color: '#1A1A1A',
                  resize: 'none',
                }}
                data-testid="announcement-body-input"
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="px-3 py-1 text-xs transition-colors"
                  style={{ backgroundColor: '#F5F7F8', color: '#1A1A1A', borderRadius: '4px' }}
                >
                  Add Emoji
                </button>
              </div>
              {showEmojiPicker && (
                <div className="mt-2 p-3 bg-white" style={{ border: '1px solid #E2E5E7', borderRadius: '6px' }}>
                  <div className="grid grid-cols-8 gap-2">
                    {COMMON_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => insertEmoji(emoji)}
                        className="text-xl hover:bg-gray-100 p-1 rounded"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {errors.body && (
                <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.body}</p>
              )}
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  className="w-full px-4 py-2 text-sm"
                  style={{
                    border: errors.start_date ? '1px solid #EF4444' : '1px solid #E2E5E7',
                    borderRadius: '6px',
                    color: '#1A1A1A',
                  }}
                />
                {errors.start_date && (
                  <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.start_date}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  className="w-full px-4 py-2 text-sm"
                  style={{
                    border: errors.end_date ? '1px solid #EF4444' : '1px solid #E2E5E7',
                    borderRadius: '6px',
                    color: '#1A1A1A',
                  }}
                />
                {errors.end_date && (
                  <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.end_date}</p>
                )}
              </div>
            </div>

            {/* Color Options */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                Color Theme
              </label>
              <div className="grid grid-cols-4 gap-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => handleInputChange('color', color.value)}
                    className="px-4 py-2 text-sm font-medium transition-all"
                    style={{
                      backgroundColor: formData.color === color.value ? color.bg : '#F5F7F8',
                      color: formData.color === color.value ? color.text : '#1A1A1A',
                      borderRadius: '6px',
                      border: formData.color === color.value ? 'none' : '1px solid #E2E5E7',
                    }}
                  >
                    {color.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Link URL */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                Link URL (optional)
              </label>
              <input
                type="url"
                value={formData.link_url}
                onChange={(e) => handleInputChange('link_url', e.target.value)}
                placeholder="https://example.com"
                className="w-full px-4 py-2 text-sm"
                style={{ border: '1px solid #E2E5E7', borderRadius: '6px', color: '#1A1A1A' }}
              />
            </div>

            {/* Submit Button */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="w-full px-6 py-3 text-sm font-medium text-white transition-colors flex items-center justify-center gap-2"
              style={{
                backgroundColor: loading ? '#6B7280' : '#10614E',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
              data-testid="announcement-submit-btn"
            >
              <Save size={18} />
              {loading
                ? editId ? 'Updating...' : 'Creating...'
                : editId ? 'Update Announcement' : 'Create Announcement'}
            </button>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Live Preview */}
          <div
            className="bg-white p-6"
            style={{ borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #E2E5E7' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Eye size={20} style={{ color: '#10614E' }} />
              <h2 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Live Preview</h2>
            </div>
            {formData.body ? (
              <div
                className="px-4 py-3 text-sm font-medium text-center"
                style={{ backgroundColor: selectedColor.bg, color: selectedColor.text, borderRadius: '4px' }}
              >
                {formData.body}
              </div>
            ) : (
              <div
                className="px-4 py-3 text-sm text-center"
                style={{ color: '#6B7280', backgroundColor: '#F5F7F8', borderRadius: '4px' }}
              >
                Your announcement will appear here
              </div>
            )}
          </div>

          {/* Recent Announcements */}
          <div
            className="bg-white p-6"
            style={{ borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #E2E5E7' }}
          >
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#1A1A1A' }}>
              Recent Announcements
            </h3>
            <div className="space-y-3" data-testid="announcements-list">
              {announcements.length === 0 ? (
                <p className="text-sm" style={{ color: '#6B7280' }}>No announcements yet</p>
              ) : (
                announcements.slice(0, 5).map((announcement) => (
                  <div
                    key={announcement.id}
                    className="p-3 text-sm"
                    style={{
                      backgroundColor: editId === announcement.id ? '#F0FDF4' : '#F5F7F8',
                      borderRadius: '4px',
                      borderLeft: `3px solid ${
                        COLOR_OPTIONS.find((c) => c.value === announcement.color)?.bg || '#10614E'
                      }`,
                    }}
                    data-testid={`announcement-row-${announcement.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate" style={{ color: '#1A1A1A' }}>
                          {announcement.title}
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
                          {(announcement.body || '').substring(0, 60)}
                          {(announcement.body || '').length > 60 ? '...' : ''}
                        </div>
                        <div className="text-xs mt-1" style={{ color: '#6B7280' }}>
                          Status:{' '}
                          <span
                            className={
                              announcement.status === 'active' ? 'text-green-600 font-medium' : 'text-gray-400'
                            }
                          >
                            {announcement.status}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleEdit(announcement)}
                          className="p-1.5 rounded transition-colors hover:bg-blue-50"
                          title="Edit"
                          data-testid={`edit-announcement-${announcement.id}`}
                          style={{ color: '#3B82F6' }}
                        >
                          <Edit size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(announcement.id)}
                          className="p-1.5 rounded transition-colors hover:bg-red-50"
                          title="Delete"
                          data-testid={`delete-announcement-${announcement.id}`}
                          style={{ color: '#EF4444' }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Announcement</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this announcement? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium text-sm"
                data-testid="confirm-delete-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementBarCMS;
